import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import JsonServiceClient from 'bz-stores/lib/JsonServiceClient';
import { Button } from 'click-controls';
import * as OTPAuth from 'otpauth';
import { QRCodeSVG } from 'qrcode.react';
import React, { useState } from 'react';
import { useCallback } from 'react';
import CodeInput from 'react-code-input';
import { v4 as uuidv4 } from 'uuid';
import z from 'z';

import { Spinimate } from 'click-controls/controls/transitions/spinimate';
import cache from 'connect/web/src/cache';
import { TwoFactorUpdateSecret } from 'tm3-core/api.dtos';
import type { IMfaSetupProps } from '.';

export function AuthApp({ onSetupComplete }: IMfaSetupProps) {
  const [code, setCode] = useState('');
  const [isCorrect, setIsCorrect] = useState(false);
  const issuer = cache?.providerStore?.title ?? z.format('{TM3} - Blue Zinc IT Ltd');
  const label = cache?.providerStore?.title ?? z.format('{TM3}');
  const [secret, totp] = React.useMemo(() => {
    const secret = uuidv4();

    const totp = new OTPAuth.TOTP({
      issuer,
      label,
      algorithm: 'SHA1',
      digits: 6,
      period: 30,
      secret: OTPAuth.Secret.fromUTF8(secret)
    });
    return [secret, totp];
  }, []);

  const onSetCode = React.useCallback(
    (code: string) => {
      if (code && code.length === 6) {
        let delta = totp.validate({
          token: code,
          window: 1
        });
        setIsCorrect(delta === 0 || delta === -1);
      }
      setCode(code);
    },
    [totp]
  );

  const onSubmitChallenge = useCallback(async () => {
    const { result } = await new JsonServiceClient().post(
      new TwoFactorUpdateSecret({
        secret
      })
    );
    if (!result) return;
    onSetupComplete();
  }, [secret]);

  const authenticationUri = totp.toString();

  return (
    <form id="form-login" className="grid" role="form" onSubmit={onSubmitChallenge}>
      <div>
        <h4 className="mb-4 text-xl font-bold">Step 1 - Setup your Authenticator App</h4>

        <p className=" my-2 text-base">Scan the code below with your authenticator app.</p>
        <p className=" my-2 text-base">
          You can use authenticator app like{' '}
          <a target="_blank" href="https://2fas.com/">
            2FAs
          </a>
          .
        </p>

        <div className="flex flex-row justify-center">
          <QRCodeSVG
            className="mb-6"
            value={authenticationUri}
            fgColor="rgb(var(--color-gray-600))"
          />
        </div>

        <h4 className="mb-4 text-xl font-bold">Step 2 - Enter the code</h4>
        <div className="flex flex-row justify-center">
          <CodeInput fields={6} name="Code" inputMode="numeric" onChange={onSetCode} css={'w-3'} />

          <Spinimate show={!!isCorrect}>
            <CheckCircleIcon className="h-12 stroke-emerald-500" />
          </Spinimate>

          <Spinimate show={code.length === 6 && !isCorrect}>
            <XCircleIcon className="h-12 stroke-rose-500" />
          </Spinimate>
        </div>
      </div>

      <Button className="btn mt-8" disabled={!isCorrect} onPress={onSubmitChallenge}>
        Continue
      </Button>
    </form>
  );
}
